import { useCallback, useMemo, useState, type ComponentType } from 'react';
import { Link2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { copyTextToClipboard } from '@/lib/copyToClipboard';
import { dispatchBlogShareInteraction, type BlogShareInteractionPayload } from '@/lib/shareAnalytics';
import {
  SHARE_LINK_PLATFORM_IDS,
  type ShareLinkPlatformId,
  buildSocialShareUrl,
} from '@/lib/socialShareUrls';
import {
  ShareGlyphFacebook,
  ShareGlyphReddit,
  ShareGlyphWhatsApp,
  ShareMarkX,
} from '@/components/blog/SharePlatformGlyph';
import { toast } from '@/hooks/use-toast';
import { cn } from '@/lib/utils';

const GLYPHS: Record<ShareLinkPlatformId, ComponentType<{ className?: string }>> = {
  facebook: ShareGlyphFacebook,
  whatsapp: ShareGlyphWhatsApp,
  reddit: ShareGlyphReddit,
  x: ShareMarkX,
};

const PLATFORM_LABEL_KEY: Record<ShareLinkPlatformId, string> = {
  facebook: 'blog.share.facebook',
  whatsapp: 'blog.share.whatsapp',
  reddit: 'blog.share.reddit',
  x: 'blog.share.x',
};

export type ArticleShareBarProps = {
  /** Display title for share text */
  title: string;
  /** Canonical absolute URL (https://…) */
  canonicalUrl: string;
  postSlug: string;
  className?: string;
  /** Optional extra listener; runs after `dispatchBlogShareInteraction` (dataLayer / plausible / posthog / gtag hooks). */
  onShareInteraction?: (payload: BlogShareInteractionPayload) => void;
};

const btnBase =
  'inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-border/60 bg-background/40 text-muted-foreground transition-colors hover:border-border hover:bg-secondary/80 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.98] disabled:pointer-events-none disabled:opacity-40';

export function ArticleShareBar({
  title,
  canonicalUrl,
  postSlug,
  className,
  onShareInteraction,
}: ArticleShareBarProps) {
  const { t } = useLanguage();
  const [copyBusy, setCopyBusy] = useState(false);

  const shareParams = useMemo(
    () => ({ url: canonicalUrl, title: title.trim() || 'Figtures' }),
    [canonicalUrl, title],
  );

  const notify = useCallback(
    (payload: BlogShareInteractionPayload) => {
      dispatchBlogShareInteraction(payload);
      onShareInteraction?.(payload);
    },
    [onShareInteraction],
  );

  const openShare = useCallback(
    (platform: ShareLinkPlatformId) => {
      if (!canonicalUrl) return;
      try {
        const href = buildSocialShareUrl(platform, shareParams);
        window.open(href, '_blank', 'noopener,noreferrer');
        notify({
          action: 'share_click',
          platform,
          slug: postSlug,
          url: canonicalUrl,
        });
      } catch {
        /* invalid url — skip */
      }
    },
    [canonicalUrl, postSlug, notify, shareParams],
  );

  const handleCopy = useCallback(async () => {
    if (!canonicalUrl || copyBusy) return;
    setCopyBusy(true);
    const ok = await copyTextToClipboard(canonicalUrl);
    setCopyBusy(false);
    if (ok) {
      toast({ title: t('blog.share.copied') });
      notify({
        action: 'copy_link',
        platform: 'copy_link',
        slug: postSlug,
        url: canonicalUrl,
      });
    } else {
      toast({
        title: t('blog.share.copyFailed'),
        variant: 'destructive',
      });
    }
  }, [canonicalUrl, copyBusy, postSlug, notify, t]);

  if (!canonicalUrl || !canonicalUrl.startsWith('http')) {
    return null;
  }

  return (
    <div
      className={cn('flex flex-col gap-2 pt-3', className)}
      role="group"
      aria-label={t('blog.share.groupLabel')}
    >
      <span className="font-body text-[10px] tracking-[0.2em] text-muted-foreground uppercase">
        {t('blog.share.label')}
      </span>
      <div className="flex flex-wrap items-center gap-2">
        {SHARE_LINK_PLATFORM_IDS.map((id) => {
          const Glyph = GLYPHS[id];
          const label = t(PLATFORM_LABEL_KEY[id]);
          return (
            <button
              key={id}
              type="button"
              className={btnBase}
              aria-label={label}
              title={label}
              onClick={() => openShare(id)}
            >
              <Glyph className="h-3.5 w-3.5" />
            </button>
          );
        })}
        <button
          type="button"
          className={btnBase}
          aria-label={t('blog.share.copy')}
          title={t('blog.share.copy')}
          onClick={() => void handleCopy()}
          disabled={copyBusy}
        >
          <Link2 className="h-3.5 w-3.5" strokeWidth={1.75} aria-hidden />
        </button>
      </div>
    </div>
  );
}
